import { useCallback, useRef } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Button, Dropdown } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import { useContainerDimensions } from "utils/useContainerDimensions";
import util from "utils/utils";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas/useIdeaQueryParams";

import type { IdeaImpactsCache, IdeaPreviewImpactMeasure } from "../../types";
import Impact from "./Impact";

const IdeaImpactHeader = styled.div`
  > span {
    display: block;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }
  .impact-name {
  }
  .impact-description {
    opacity: 0.7;
  }
`;

const IdeaImpactDropdownItem = styled(Dropdown.Item)`
  display: flex !important;
  overflow: hidden;
  text-overflow: ellipsis;
  > i {
    width: 10px;
  }
  ${IdeaImpactHeader} {
    max-width: 95%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: normal;
  }
`;

type IdeaImpactsPerIdea = {
  totalImpacts?: IdeaPreviewImpactMeasure[];
  completedImpactStages?: unknown;
};

const getTotalImpacts = (
  entry: IdeaImpactsPerIdea | undefined,
): IdeaPreviewImpactMeasure[] =>
  Array.isArray(entry?.totalImpacts) ? entry.totalImpacts : [];

const asImpactMeasures = (totalImpacts: unknown): IdeaPreviewImpactMeasure[] =>
  Array.isArray(totalImpacts) ?
    (totalImpacts as IdeaPreviewImpactMeasure[])
  : [];

type ImpactProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"];
  impactList: OpenAPI.Schemas["OrganisationImpact"][];
  canManage: boolean;
};

const ImpactManagement = ({ idea, impactList, canManage }: ImpactProps) => {
  const ideaId = idea._id;
  const dropdownRef = useRef<HTMLDivElement>(null!);
  const { width } = useContainerDimensions(dropdownRef);
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const queryParams = useIdeaQueryParams("board");
  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [idea.challenge, queryParams], {
      select: (data) =>
        getTotalImpacts(data.ideaImpacts?.[idea._id] as IdeaImpactsPerIdea),
    }),
  );

  const updateIdeaImpactsCache = useCallback(
    (
      updater:
        | IdeaImpactsPerIdea
        | ((
            entry: IdeaImpactsPerIdea,
          ) => IdeaImpactsPerIdea | IdeaPreviewImpactMeasure[]),
    ) => {
      api.setQueryData(
        api.challenges.getIdeaImpacts,
        [idea.challenge],
        (prev: IdeaImpactsCache | undefined) => {
          const currentEntry = (prev?.ideaImpacts?.[idea._id] ??
            {}) as IdeaImpactsPerIdea;
          const nextValue =
            typeof updater === "function" ? updater(currentEntry) : updater;
          const totalImpacts =
            Array.isArray(nextValue) ? nextValue : nextValue.totalImpacts;

          return {
            ...(prev || {}),
            ideaImpacts: {
              ...(prev?.ideaImpacts || {}),
              [idea._id]: { ...currentEntry, totalImpacts },
            },
          } as IdeaImpactsCache;
        },
      );
    },
    [idea],
  );

  const addImpact = useCallback(
    (impactId: string) => {
      api.ideas.addImpactMeasure(
        ideaId,
        impactId,
        {},
        (data) => {
          updateIdeaImpactsCache((entry) => [
            ...getTotalImpacts(entry),
            data.newImpact as IdeaPreviewImpactMeasure,
          ]);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [ideaId, updateIdeaImpactsCache],
  );

  const deleteImpact = useCallback(
    (impactId: string, impactMeasureId: string) => {
      util
        .confirm(
          `Are you sure you want to delete this ${t("generic.impact")} measure?`,
          "This action cannot be undone.",
        )
        .then(() => {
          api.ideas.removeImpactMeasure(
            idea._id,
            impactId,
            impactMeasureId,
            (response) => {
              const totalImpacts = asImpactMeasures(
                (response as { impacts?: { totalImpacts?: unknown } }).impacts
                  ?.totalImpacts,
              );
              updateIdeaImpactsCache({ totalImpacts });
            },
            (err) => {
              toast.error(err.message);
            },
          );
        })
        .catch(() => {});
    },
    [idea, t, updateIdeaImpactsCache],
  );

  const onUpdateIdeaImpact = useCallback(
    (impactId: string, data: Partial<OpenAPI.Schemas["IdeaImpact"]>) => {
      updateIdeaImpactsCache((entry) =>
        getTotalImpacts(entry).map((impact) =>
          impact.impactId === impactId ? { ...impact, ...data } : impact,
        ),
      );
    },
    [updateIdeaImpactsCache],
  );

  const onUpdateIdeaTotalImpacts = useCallback(
    (totalImpacts: IdeaPreviewImpactMeasure[]) => {
      updateIdeaImpactsCache({ totalImpacts });
    },
    [updateIdeaImpactsCache],
  );

  if (!(impactList?.length > 0)) return null;
  return (
    <div ref={dropdownRef}>
      <p>
        How will this {t("generic.idea")} {t("generic.impact")} the{" "}
        {t("generic.challenge")}?
      </p>

      {(
        canManage ||
        util.hasPermission(
          user!,
          "challenge.editProjectImpacts",
          idea?.challenge,
        )
      ) ?
        <Dropdown
          icon={null}
          pointing="top"
          fluid
          trigger={
            <Button
              size="small"
              primary
              icon="plus"
              content={`Add new ${t("generic.impact")}`}
            />
          }
        >
          <Dropdown.Menu
            style={{
              left: "auto",
              right: "auto",
              width,
              maxHeight: 250,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {impactList.map((i) => (
              <IdeaImpactDropdownItem
                key={i.name}
                icon={i.icon ?? "tag"}
                content={
                  <IdeaImpactHeader>
                    <span className="impact-name">{i.name}</span>
                    <span className="impact-description">{i.description}</span>
                  </IdeaImpactHeader>
                }
                onClick={() => addImpact(i._id)}
                style={{ width }}
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      : null}

      {ideaImpactsQuery.data?.map((i: IdeaPreviewImpactMeasure) => (
        <Impact
          key={i._id}
          idea={idea}
          impact={i}
          onUpdateIdeaImpact={onUpdateIdeaImpact}
          deleteImpact={deleteImpact}
          onUpdateIdeaTotalImpacts={onUpdateIdeaTotalImpacts}
          canEdit={
            canManage ||
            util.hasPermission(
              user!,
              "challenge.editProjectImpacts",
              idea?.challenge,
            )
          }
        />
      ))}
    </div>
  );
};

export default ImpactManagement;
